import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  supabase,
  SUPABASE_FUNCTIONS_URL,
  SUPABASE_ANON_KEY,
} from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import type { VoucherAllocation } from "@/lib/db-types";

export function useVoucherAllocations(voucherId: string | undefined) {
  return useQuery({
    queryKey: ["voucher-allocations", voucherId],
    enabled: !!voucherId,
    queryFn: async (): Promise<VoucherAllocation[]> => {
      const { data, error } = await supabase
        .from("voucher_allocations")
        .select("*")
        .eq("voucher_id", voucherId!)
        .order("created_at");
      if (error) throw error;
      return (data ?? []) as VoucherAllocation[];
    },
  });
}

export interface SaveAllocationsInput {
  voucherId: string;
  projectId?: string;
  allocations: Array<{
    budget_line_id: string | null;
    amount: number;
    description: string | null;
    po_account_id: number | null;
    source: "po" | "manual" | "vision";
  }>;
}

/** Erstatter alle allokeringer for et bilag atomisk (delete + insert). */
export function useSaveVoucherAllocations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ voucherId, allocations }: SaveAllocationsInput) => {
      const { error: delErr } = await supabase
        .from("voucher_allocations")
        .delete()
        .eq("voucher_id", voucherId);
      if (delErr) throw delErr;
      if (allocations.length > 0) {
        const { error: insErr } = await supabase
          .from("voucher_allocations")
          .insert(allocations.map((a) => ({ ...a, voucher_id: voucherId })));
        if (insErr) throw insErr;
      }
    },
    onSettled: async (_d, _e, vars) => {
      await invalidateFor(qc, "voucher_allocations", {
        voucherId: vars.voucherId,
        projectId: vars.projectId,
      });
    },
  });
}

export interface SuggestAllocationsInput {
  posts: Array<{
    description: string | null;
    amount: number;
    po_account_id: number | null;
  }>;
  budgetLines: Array<{
    id: string;
    description: string | null;
    phase: string | null;
    cost_type: string | null;
  }>;
}

export interface AllocationSuggestion {
  index: number;
  budget_line_id: string | null;
  confidence: number;
}

/**
 * Kaller `suggest-voucher-allocations` edge function for å foreslå hvilken
 * budsjettlinje hver bilagspost hører til. Edge functionen returnerer
 * `{ suggestions: [] }` ved feil, så hooken kaster ikke ved tom respons.
 */
export function useSuggestAllocations() {
  return useMutation({
    mutationFn: async (
      input: SuggestAllocationsInput,
    ): Promise<AllocationSuggestion[]> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(
        `${SUPABASE_FUNCTIONS_URL}/suggest-voucher-allocations`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${session?.access_token ?? ""}`,
            apikey: SUPABASE_ANON_KEY,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(input),
        },
      );
      if (!res.ok) return [];
      const data = (await res.json()) as { suggestions?: AllocationSuggestion[] };
      return data.suggestions ?? [];
    },
  });
}
